/**
 * ADR-036 — the time line at the top of Else's Vandaag: "Het is
 * woensdagmiddag." with "14 oktober, tien over één" under it. Pure and
 * client-safe: the server renders it, the page ticks it every minute.
 * The time is said the way people say it, to the nearest five minutes;
 * nothing counts down.
 */

export type Dagdeel = 'nacht' | 'ochtend' | 'middag' | 'avond'

const UREN = [
	'twaalf',
	'één',
	'twee',
	'drie',
	'vier',
	'vijf',
	'zes',
	'zeven',
	'acht',
	'negen',
	'tien',
	'elf',
]

const PARTS = new Intl.DateTimeFormat('nl-NL', {
	timeZone: 'Europe/Amsterdam',
	weekday: 'long',
	day: 'numeric',
	month: 'long',
	hour: 'numeric',
	minute: 'numeric',
	hourCycle: 'h23',
})

function partsOf(now: Date) {
	const p = Object.fromEntries(
		PARTS.formatToParts(now).map((x) => [x.type, x.value]),
	)
	return {
		weekday: p.weekday!,
		day: p.day!,
		month: p.month!,
		hour: Number(p.hour),
		minute: Number(p.minute),
	}
}

export function dagdeelOfHour(hour: number): Dagdeel {
	if (hour < 6) return 'nacht'
	if (hour < 12) return 'ochtend'
	if (hour < 18) return 'middag'
	return 'avond'
}

/** The time to the nearest five minutes; 12:58 is 13:00. */
function afgerond(hour: number, minute: number): { h: number; m: number } {
	const m = Math.round(minute / 5) * 5
	return m === 60 ? { h: (hour + 1) % 24, m: 0 } : { h: hour, m }
}

/** "tien over één", "half drie", "kwart voor twaalf" (nearest five minutes) */
export function tijdInWoorden(hour: number, minute: number): string {
	const { h, m } = afgerond(hour, minute)
	const dit = UREN[h % 12]!
	const volgend = UREN[(h + 1) % 12]!
	switch (m) {
		case 0:
			return `${dit} uur`
		case 5:
			return `vijf over ${dit}`
		case 10:
			return `tien over ${dit}`
		case 15:
			return `kwart over ${dit}`
		case 20:
			return `tien voor half ${volgend}`
		case 25:
			return `vijf voor half ${volgend}`
		case 30:
			return `half ${volgend}`
		case 35:
			return `vijf over half ${volgend}`
		case 40:
			return `tien over half ${volgend}`
		case 45:
			return `kwart voor ${volgend}`
		case 50:
			return `tien voor ${volgend}`
		default:
			return `vijf voor ${volgend}`
	}
}

export type Tijdregel = { kop: string; onder: string }

/**
 * The day part follows the time she reads (05:58 says "zes uur", so it is
 * ochtend). At night it is just "Het is nacht.": "donderdagnacht" would
 * mean the night after Thursday, a day later than it is.
 */
export function tijdregel(now: Date): Tijdregel {
	const p = partsOf(now)
	const deel = dagdeelOfHour(afgerond(p.hour, p.minute).h)
	const tijd = tijdInWoorden(p.hour, p.minute)
	if (deel === 'nacht')
		return {
			kop: 'Het is nacht.',
			onder: `${p.weekday} ${p.day} ${p.month}, ${tijd}`,
		}
	return {
		kop: `Het is ${p.weekday}${deel}.`,
		onder: `${p.day} ${p.month}, ${tijd}`,
	}
}

/** The part of the day it is now, the same one the time line says. */
export function dagdeelNu(now: Date): Dagdeel {
	const p = partsOf(now)
	return dagdeelOfHour(afgerond(p.hour, p.minute).h)
}
